import { useEffect, useRef, useState } from 'react'
import {
  ComcastErrorType,
  triggerSentryError,
} from '../../../../../shared/helpers.js'
import { getSearch } from '../../../actions/contentActions.js'
import { useAppSelector } from '../../../hooks/useStore'
import tts from '../../../lib/TTS.js'
import { contentServiceGet } from '../../../lib/utils.js'
import { Collection } from '../../../reducers/types/reducerTypes'
import {
  checkShowsAndVideoAssetContentExistence,
  concatNextContent,
  filterFranchiseContent,
} from '../utils'

const TTS_ERROR_MESSAGE =
  'Sorry, there was an unexpected error with search. Please check back later.'
const TTS_SEARCHING_MESSAGE = (searchQuery: string) =>
  `Searching for ${searchQuery}`
const SEARCH_ERROR_MESSAGE = 'An error occurred while searching'
const SERACH_RESULT_ERROR = 'setSearchResult'
const INITIAL_SEARCH_RESULT = 'setInitialSearchResults'
const NEXT_CAROUSEL_RESULT = 'setNextCarouselResult'

// eslint-disable-next-line import/prefer-default-export
export const useSearchResult = () => {
  const [collectionsReady, setCollectionsReady] = useState(false)
  const [nextContentUrl, setNextContentUrl] = useState('')
  const [searchResultCollection, setSearchResultCollections] = useState<
    Collection<unknown>[]
  >([])
  const [initialCollection, setInitialCollection] = useState<
    Collection<unknown>[]
  >([])
  const stationId = useAppSelector((state) => state.station.station_id)
  const searchQueryRef = useRef<string>('')
  const [fetchingNextCarouselResult, setFetchingNextCarouselResult] =
    useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (collectionsReady && searchResultCollection.length === 0) {
      tts.addTextToRead(
        `We couldn't find any results for ${searchQueryRef.current}.`
      )
    }
  }, [setCollectionsReady, searchResultCollection])

  const handleError = (err: Error, errorString: string) => {
    setError(err)
    tts.addTextToRead(TTS_ERROR_MESSAGE)
    triggerSentryError(errorString, 'SearchScreen', {
      errorMessage: err.message ?? 'Undefined search message (default)',
      errorLocation: 'SearchScreen',
      errorType: ComcastErrorType.network,
    })
  }

  const setSearchResult = async (searchQuery: string) => {
    tts.addTextToRead(TTS_SEARCHING_MESSAGE)
    setCollectionsReady(false)
    try {
      searchQueryRef.current = searchQuery
      const response = await getSearch(stationId, searchQuery)
      const hasContentToDisplay = checkShowsAndVideoAssetContentExistence(
        response.collections
      )
      if (response) {
        const filteredResponse = filterFranchiseContent(response.collections)
        setSearchResultCollections(hasContentToDisplay ? filteredResponse : [])
        setNextContentUrl(response.meta?.links?.next || '')
        setCollectionsReady(true)
      } else {
        throw new Error(SEARCH_ERROR_MESSAGE)
      }
    } catch (err: unknown) {
      setCollectionsReady(true)
      handleError(err as Error, SERACH_RESULT_ERROR)
    }
  }

  const setInitialSearchResults = async () => {
    setCollectionsReady(false)

    if (initialCollection.length > 0) {
      setSearchResultCollections(initialCollection)
      setCollectionsReady(true)
      return
    }

    try {
      const response = await getSearch(stationId, '')
      const collections = response.collections.filter(
        (element: any) => typeof element.content !== 'undefined'
      )

      setSearchResultCollections(collections)
      setCollectionsReady(true)
      setInitialCollection(collections)
    } catch (err: unknown) {
      console.error(err)
      setCollectionsReady(true)
      handleError(err as Error, INITIAL_SEARCH_RESULT)
    }
  }

  const setNextCarouselResult = async () => {
    if (!fetchingNextCarouselResult && nextContentUrl) {
      setFetchingNextCarouselResult(true)
      try {
        const response = await contentServiceGet(nextContentUrl)
        const filteredResponse = filterFranchiseContent(response.collections)

        const nextShowResults = concatNextContent(
          filteredResponse,
          'show-results',
          searchResultCollection
        )
        const nextVideoResults = concatNextContent(
          filteredResponse,
          'videoasset-results',
          searchResultCollection
        )

        setNextContentUrl(response.meta.links.next || '')
        setFetchingNextCarouselResult(false)

        const auxCollections = [...searchResultCollection]
        auxCollections.forEach((collection) => {
          if (collection.slug === 'show-results') {
            // eslint-disable-next-line no-param-reassign
            collection.content = nextShowResults.content
          } else if (collection.slug === 'videoasset-results') {
            // eslint-disable-next-line no-param-reassign
            collection.content = nextVideoResults.content
          }
        })

        setSearchResultCollections(auxCollections)
      } catch (err: unknown) {
        console.error(err)
        setFetchingNextCarouselResult(false)
        setSearchResultCollections([])
        handleError(err as Error, NEXT_CAROUSEL_RESULT)
      }
    }
  }

  return {
    searchQueryRef,
    collectionsReady,
    searchResultCollection,
    initialCollection,
    setSearchResult,
    setInitialSearchResults,
    setNextCarouselResult,
    error,
  }
}
